/**
 * Hand-mirrored client types for `t/:slug/api/v1/setup`
 * (`apps/api/src/setup/{contract,module-answers}.ts`). Mirrors the same
 * "hand copy, not a cross-package import" pattern every other
 * `lib/types/*.ts` file in this app already uses (see e.g.
 * `masterDataConfig.ts`'s own doc comment) — apps/api is a Node-only
 * package, not importable from this browser bundle.
 *
 * This file describes the wire contract only; it adds no client-only
 * fields and invents no local setup state (AGENTS.md task scope: "Do not
 * create fake local setup state").
 */

export const SETUP_PROGRESS_STATUSES = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'COMPLETED',
  'SKIPPED',
  'BLOCKED',
] as const;

export type SetupProgressStatus = (typeof SETUP_PROGRESS_STATUSES)[number];

/** Phase B — one step per real product module. */
export const MODULE_STEP_KEYS = [
  'sales-crm-setup',
  'inventory-setup',
  'procurement-setup',
  'finance-setup',
  'projects-setup',
] as const;
export type ModuleStepKey = (typeof MODULE_STEP_KEYS)[number];

/** Phase C — team gate (no payload; reuses the real membership-admin endpoints) and previous-data intent. */
export const TEAM_STEP_KEY = 'team' as const;
export const PREVIOUS_DATA_STEP_KEY = 'previous-data' as const;

export const FREEFORM_STEP_KEYS = [...MODULE_STEP_KEYS, PREVIOUS_DATA_STEP_KEY] as const;
export type FreeformStepKey = (typeof FREEFORM_STEP_KEYS)[number];

export const SETUP_STEP_KEYS = [
  'welcome',
  'base-organization',
  ...MODULE_STEP_KEYS,
  TEAM_STEP_KEY,
  PREVIOUS_DATA_STEP_KEY,
] as const;
export type SetupStepKey = (typeof SETUP_STEP_KEYS)[number];

/** Mirrors `STEP_REQUIRES` — a step is BLOCKED until its prerequisite is COMPLETED or SKIPPED. */
export const STEP_REQUIRES: Partial<Record<SetupStepKey, SetupStepKey>> = {
  'base-organization': 'welcome',
  'sales-crm-setup': 'base-organization',
  'inventory-setup': 'base-organization',
  'procurement-setup': 'base-organization',
  'finance-setup': 'base-organization',
  'projects-setup': 'base-organization',
  [TEAM_STEP_KEY]: 'base-organization',
  [PREVIOUS_DATA_STEP_KEY]: 'base-organization',
};

export const SETUP_INVARIANTS = {
  calendar: 'jalali',
  timezone: 'Asia/Tehran',
  currency: 'IRR',
} as const;

export type SetupVatMode = 'enabled' | 'disabled' | 'skipped';

/** Section 1 ("شناخت کسب‌وکار") — folded into base-organization's own
 * payload rather than a new step (see contract.ts on the API side for why).
 * `displayName` is deliberately not asked here — it's already
 * `organizationName` from trial signup. */
export type SetupPersonType = 'individual' | 'company' | 'undecided';
export type SetupIndustry =
  'trade' | 'retail' | 'services' | 'manufacturing' | 'contracting' | 'distribution' | 'other';
export type SetupActivityModel = 'goods' | 'services' | 'projects';
export type SetupTeamSizeRange = 'solo' | '2-5' | '6-15' | '16-30' | 'more';
/** Display-only preference (AGENTS.md §9) — every stored amount stays
 * integer Rial regardless of this value. */
export type SetupAmountDisplayUnit = 'toman' | 'rial';

export interface SetupFiscalYearInput {
  readonly code: string;
  readonly name: string;
  /** Business date, `YYYY-MM-DD` (Gregorian on the wire — Jalali is a display-only boundary, see `lib/date/`). */
  readonly startDate: string;
  readonly endDate: string;
}

export interface SetupDocumentNumberingInput {
  readonly prefix: string;
  /** Integer 1-12. */
  readonly padding: number;
  readonly series: string;
}

export interface SetupPrimaryLocationInput {
  readonly code: string;
  readonly name: string;
}

/**
 * What prints on official documents (pre-invoice/invoice headers, SOT
 * §12.12) — section 3 ("اطلاعاتی که روی اسناد شما نمایش داده می‌شود").
 * Entirely optional: never required to complete base-organization, and
 * print templates render "—" for a field left unset. `legalName`/
 * `nationalId` are the registered-company pair; `ownerFirstName`/
 * `ownerLastName` are the natural-person pair — the UI shows only the pair
 * matching `personType`.
 */
export interface SetupLegalIdentityInput {
  readonly invoiceDisplayName: string | null;
  readonly legalName: string | null;
  readonly nationalId: string | null;
  readonly ownerFirstName: string | null;
  readonly ownerLastName: string | null;
  readonly economicCode: string | null;
  readonly phone: string | null;
  readonly address: string | null;
}

/**
 * Step 2 payload. `calendar`/`timezone`/`currency` are fixed invariants —
 * this client never sends them (the backend accepts them only if they
 * match `SETUP_INVARIANTS`, and treats an omitted key as "keep current"),
 * it only ever renders them read-only.
 */
export interface BaseOrganizationPayload {
  readonly calendar?: string;
  readonly timezone?: string;
  readonly currency?: string;
  readonly personType: SetupPersonType | null;
  readonly industry: SetupIndustry | null;
  readonly industryOtherNote: string | null;
  readonly activityModel: readonly SetupActivityModel[];
  readonly teamSizeRange: SetupTeamSizeRange | null;
  readonly fiscalYear: SetupFiscalYearInput | null;
  readonly vat: SetupVatMode | null;
  readonly amountDisplayUnit: SetupAmountDisplayUnit | null;
  readonly documentNumbering: SetupDocumentNumberingInput | null;
  readonly primaryLocation: SetupPrimaryLocationInput | null;
  readonly legalIdentity: SetupLegalIdentityInput | null;
}

/** A `PATCH .../base-organization` body — every key optional, `null` clears, an omitted key keeps the previous value. */
export type BaseOrganizationPatch = Partial<BaseOrganizationPayload>;

// ---------------------------------------------------------------------------
// Phase B module Q&A answers — mirrors apps/api/src/setup/module-answers.ts.
// Every field is optional; answering nothing is a valid, completable state.
// ---------------------------------------------------------------------------

export type SalesModel = 'b2b' | 'b2c' | 'both';
export type LeadSource =
  'referral' | 'social' | 'website' | 'phone' | 'ads' | 'in-person' | 'marketplace' | 'other';
export type QuoteInProcess = 'yes' | 'no' | 'later';
export type OrderChannel =
  'phone' | 'in-person' | 'whatsapp' | 'instagram' | 'website' | 'salesperson';

export interface SalesCrmAnswers {
  readonly salesModel?: SalesModel;
  readonly leadSources?: readonly LeadSource[];
  readonly leadSourcesOtherNote?: string;
  readonly quoteInProcess?: QuoteInProcess;
  readonly orderChannel?: readonly OrderChannel[];
}

export type WarehouseModel = 'single' | 'multiple';
export type CountingUnit =
  'count' | 'package' | 'carton' | 'kg' | 'g' | 'metre' | 'sqm' | 'litre' | 'other';

export interface InventoryAnswers {
  readonly managesInventory?: 'yes' | 'no' | 'later';
  readonly warehouseModel?: WarehouseModel;
  readonly warehouseNames?: readonly string[];
  readonly countingUnits?: readonly CountingUnit[];
  readonly countingUnitsOtherNote?: string;
  readonly serialTracking?: boolean;
  readonly expiryTracking?: boolean;
}

export type PrRequirement = 'always' | 'sometimes' | 'never';

export interface ProcurementAnswers {
  readonly prRequirement?: PrRequirement;
  readonly fixedSuppliers?: 'yes' | 'no';
}

/** Mirrors `CHART_OF_ACCOUNTS_CHOICES` in apps/api/src/setup/contract.ts (ERP-TBD-1). */
export type ChartOfAccountsChoice = 'default' | 'import-own' | 'decide-later';

export interface FinanceAnswers {
  readonly chartOfAccountsChoice?: ChartOfAccountsChoice;
}

export type TaskManagementModel = 'within-projects' | 'standalone' | 'both';

export interface ProjectsAnswers {
  readonly taskManagementModel?: TaskManagementModel;
}

export type PreviousDataIntent = 'yes' | 'no' | 'unsure';
export type PreviousDataScope =
  'customers' | 'products' | 'inventory' | 'accounts' | 'documents' | 'projects';
export type PreviousDataSource = 'excel' | 'accounting-software' | 'crm' | 'multiple' | 'other';

export interface PreviousDataAnswers {
  /** Intent only — answering this never triggers a real import/migration. */
  readonly hasPreviousData?: PreviousDataIntent;
  readonly dataScope?: readonly PreviousDataScope[];
  readonly dataSource?: PreviousDataSource;
  readonly dataSourceOtherNote?: string;
}

export interface SetupStepView {
  readonly key: SetupStepKey;
  readonly status: SetupProgressStatus;
  readonly payload: Record<string, unknown>;
  readonly blockedReason: string | null;
}

export interface SetupView {
  readonly status: SetupProgressStatus;
  readonly currentStepKey: SetupStepKey;
  readonly invariants: typeof SETUP_INVARIANTS;
  /** Chart choices this build offers; `import-own` appears only once the server opens it. */
  readonly chartOfAccountsChoices?: readonly ChartOfAccountsChoice[];
  readonly steps: readonly SetupStepView[];
  readonly canResume: boolean;
  readonly canSkipToHome: boolean;
  /** Server-authoritative; never inferred from permissions on the client. */
  readonly isTenantAdmin?: boolean;
}

export function setupStepView(view: SetupView, key: SetupStepKey): SetupStepView {
  const step = view.steps.find((item) => item.key === key);
  if (!step) {
    // Defensive only — the real contract always returns every step.
    return { key, status: 'NOT_STARTED', payload: {}, blockedReason: null };
  }
  return step;
}

/** `base-organization`'s saved payload, narrowed from its untyped `Record<string, unknown>` wire shape. */
export function baseOrganizationPayloadOf(step: SetupStepView): BaseOrganizationPayload {
  const raw = step.payload as Partial<BaseOrganizationPayload>;
  return {
    personType: raw.personType ?? null,
    industry: raw.industry ?? null,
    industryOtherNote: raw.industryOtherNote ?? null,
    activityModel: raw.activityModel ?? [],
    teamSizeRange: raw.teamSizeRange ?? null,
    fiscalYear: raw.fiscalYear ?? null,
    vat: raw.vat ?? null,
    amountDisplayUnit: raw.amountDisplayUnit ?? null,
    documentNumbering: raw.documentNumbering ?? null,
    primaryLocation: raw.primaryLocation ?? null,
    legalIdentity: raw.legalIdentity ?? null,
  };
}

/** Narrows a freeform step's untyped payload — same "cast, never re-validate client-side" pattern as `baseOrganizationPayloadOf`. */
export function moduleAnswersOf<T>(step: SetupStepView): T {
  return step.payload as T;
}

export function setupStepDone(status: SetupProgressStatus): boolean {
  return status === 'COMPLETED' || status === 'SKIPPED';
}

export function isSetupStepKey(value: unknown): value is SetupStepKey {
  return typeof value === 'string' && (SETUP_STEP_KEYS as readonly string[]).includes(value);
}

export const SETUP_STATUS_LABEL: Record<SetupProgressStatus, string> = {
  NOT_STARTED: 'شروع‌نشده',
  IN_PROGRESS: 'در حال انجام',
  COMPLETED: 'تکمیل‌شده',
  SKIPPED: 'رد شده',
  BLOCKED: 'مسدود',
};

export const SETUP_STEP_LABEL: Record<SetupStepKey, string> = {
  welcome: 'خوش‌آمدگویی',
  'base-organization': 'تنظیمات پایه سازمان',
  'sales-crm-setup': 'فروش و CRM',
  'inventory-setup': 'انبار',
  'procurement-setup': 'خرید و تدارکات',
  'finance-setup': 'مالی و حسابداری',
  'projects-setup': 'پروژه‌ها و کارها',
  team: 'اعضای تیم',
  'previous-data': 'اطلاعات قبلی',
};
